import { useLocation } from 'react-router-dom'
import { introductionFor } from '@/lib/introduction'
import { useProfile } from '@/auth/ProfileContext'
import { isScienceExpert } from '@/lib/programs'
import { Icon } from '@/components/Icon'
/**
 * Renders the expert or science guide as collapsible chapters. Edit the markdown for guide text.
 *
 * Structure expected from the markdown:
 *   # Title            page title, followed by an optional intro
 *   ## Chapter         section; text before the first ### is the chapter intro
 *   ### Item           one collapsible entry inside the chapter
 * The first chapter introduces the platform; the second shows the numbered task flow.
 * Both stay visible. Later chapters contain collapsible reference instructions.
 */
import guide from '@docs/sme-guide.md?raw'
import scienceGuide from '@docs/science-expert-guide.md?raw'
import { Markdown } from '@/components/Markdown'
import { Fold } from '@/components/Section'
import './guide.css'

interface Item { title: string; body: string[] }
interface Chapter { title: string; intro: string[]; items: Item[] }

function parse(md: string): { title: string; intro: string[]; chapters: Chapter[] } {
  const doc = { title: 'Guide', intro: [] as string[], chapters: [] as Chapter[] }
  let chapter: Chapter | null = null
  let item: Item | null = null
  for (const line of md.split('\n')) {
    if (line.startsWith('# ')) { doc.title = line.slice(2).trim(); continue }
    if (line.startsWith('## ')) { chapter = { title: line.slice(3).trim(), intro: [], items: [] }; item = null; doc.chapters.push(chapter); continue }
    if (line.startsWith('### ') && chapter) { item = { title: line.slice(4).trim(), body: [] }; chapter.items.push(item); continue }
    if (item) item.body.push(line)
    else if (chapter) chapter.intro.push(line)
    else doc.intro.push(line)
  }
  return doc
}

const DOC = parse(guide)
const SCIENCE_DOC = parse(scienceGuide)

export default function SmeGuide() {
  const science = isScienceExpert(useProfile().profile)
  const doc = science ? SCIENCE_DOC : DOC
  const [WELCOME, FLOW, ...DETAILS] = doc.chapters
  const introductionCards = introductionFor(science)
  const location = useLocation()
  const reference = location.state?.tutorialReference as number | undefined
  return (
    <div className="narrow expert-guide">
      <div className="page-head"><div><h1>{doc.title}</h1><p className="meta">{science ? 'Read the steps and instructions for scientific work.' : 'Learn the workflow, then find the instructions you need.'}</p></div></div>
      <section className="guide-start" data-tour="guide-replay"><span className="guide-start-icon"><Icon name="sparkles" size={26} /></span><div><h2>Start with a guided introduction</h2><p>{science ? 'A short tour of workflows, templates, and scientific reviews.' : 'A short tour of your workspace, tasks, and reviews.'}</p></div><button onClick={() => window.dispatchEvent(new Event('expert-introduction'))}><Icon name="play" size={16} />Replay introduction</button></section>
      {doc.intro.join('\n').trim() && <div className="md"><Markdown source={doc.intro.join('\n')} /></div>}
      <section className="guide-welcome" aria-labelledby="guide-welcome-title">
        <h2 id="guide-welcome-title">{WELCOME.title}</h2>
        <Markdown source={WELCOME.intro.join('\n')} />
        {WELCOME.items.map(item => (
          <div key={item.title}>
            <h3>{item.title}</h3>
            <Markdown source={item.body.join('\n')} />
          </div>
        ))}
      </section>
      <section className="guide-flow" aria-labelledby="guide-flow-title">
        <h2 id="guide-flow-title">{FLOW.title}</h2>
        <Markdown source={FLOW.intro.join('\n')} />
        <ol className="guide-steps">
          {FLOW.items.map((step, i) => (
            <li key={step.title}>
              <div className="guide-step-heading">
                <span className="guide-step-number" aria-hidden="true">{i + 1}</span>
                <h3>{step.title}</h3>
              </div>
              <Markdown source={step.body.join('\n')} />
            </li>
          ))}
        </ol>
      </section>
      <section className="guide-reference"><h2>Introduction reference</h2><p className="muted small">Read any part of the tour here.</p>{introductionCards.map((card, i) => <details className="fold" key={card.title} data-tour={`reference-${i}`} open={reference === i ? true : undefined}><summary>{card.title}</summary><div className="fold-body"><Markdown source={card.body} /></div></details>)}</section>
      <h2 className="guide-details-title">Details &amp; common questions</h2>
      <nav className="guide-toc" aria-label="Guide details">
        {DETAILS.map((c, i) => <a key={i} href={`#chapter-${i + 3}`}>{c.title}</a>)}
      </nav>
      {DETAILS.map((c, i) => (
        <section className="chapter" id={`chapter-${i + 3}`} key={i}>
          <h2>{c.title}</h2>
          {c.intro.join('\n').trim() && <div className="intro"><Markdown source={c.intro.join('\n')} /></div>}
          {c.items.map((it, j) => <Fold key={j} title={it.title}><Markdown source={it.body.join('\n')} /></Fold>)}
        </section>
      ))}
    </div>
  )
}
